<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useOrganization, useAuth, useUser, useClerk } from '@clerk/vue'
import { useQueryClient } from '@tanstack/vue-query'

const router = useRouter()
const isOpen = ref(false)
const switcherRef = ref(null)

const { organization } = useOrganization()
const { has } = useAuth()
const { user, isLoaded } = useUser()
const clerk = useClerk()

const canManageSettings = () => {
  if (!has.value) return false
  return has.value({ role: 'org:owner' }) || has.value({ role: 'org:admin' })
}

const handleClickOutside = (event) => {
  if (isOpen.value && switcherRef.value && !switcherRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

const switchOrg = async (orgId) => {
  try {
    await clerk.value.setActive({ organization: orgId })
    isOpen.value = false
    window.location.href = '/'
  } catch (error) {
    console.error('Failed to switch workspace:', error)
  }
}
</script>

<template>
  <div class="relative" v-if="isLoaded && user" ref="switcherRef">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
    >
      {{ organization?.name || 'Select Workspace' }}
      <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-100 z-[1000]"
      >
        <div class="p-2 border-b border-gray-100">
          <p class="text-xs text-gray-400 font-bold uppercase mb-1 px-2">Workspaces</p>

          <div class="max-h-[300px] overflow-y-auto">
            <button
              v-for="membership in user.organizationMemberships"
              :key="membership.organization.id"
              @click="switchOrg(membership.organization.id)"
              class="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded flex items-center justify-between cursor-pointer"
            >
              {{ membership.organization.name }}
            </button>

            <div
              v-if="!user.organizationMemberships?.length"
              class="px-2 py-1 text-sm text-gray-500 italic"
            >
              No workspaces found
            </div>
          </div>

          <div class="mt-1 pt-1 border-t border-gray-50">
            <RouterLink
              :to="{ name: 'CreateOrganization' }"
              @click="isOpen = false"
              class="w-full text-left px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded flex items-center gap-2 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create New Organization
            </RouterLink>
          </div>
        </div>

        <div class="p-2" v-if="canManageSettings()">
          <RouterLink
            :to="{ name: 'EditOrganization' }"
            @click="isOpen = false"
            class="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
          >
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Organization Settings
          </RouterLink>
        </div>
      </div>
    </transition>
  </div>
</template>
